import { KRAJ } from './kraj';
import i18n from './i18n';
export const getPanelManufacturerLogoPath = (manufacturer) => {
  const formattedManufacturer = manufacturer.toLowerCase().replace(/\s+/g, '');

  return `/panels_manufacturers/${formattedManufacturer}.jpg`;
};

export const getInverterManufacturerLogoPath = (manufacturer) => {
  const formattedManufacturer = manufacturer.toLowerCase().replace(/\s+/g, '');

  return `/inverters_manufacturers/${formattedManufacturer}.jpg`;
};

export const getMagazineManufacturerLogoPath = (manufacturer) => {
  const formattedManufacturer = manufacturer.toLowerCase().replace(/\s+/g, '');

  return `/magazines_manufacturers/${formattedManufacturer}.jpg`;
};

export const getWallboxManufacturerLogoPath = (manufacturer) => {
  const formattedManufacturer = manufacturer.toLowerCase().replace(/\s+/g, '');

  return `/wallboxes_manufacturers/${formattedManufacturer}.jpg`;
};

// ---- Presentation helpers (UI only — nie zmieniają danych w DB/API) ----

// Formatuje liczbę wg locale kraju (pl-PL: spacja jako separator tysięcy). Zwraca null gdy brak wartości.
export const formatPrice = (value, decimals = 0) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(num)) return null;
  return new Intl.NumberFormat(KRAJ.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

// Kwota z walutą kraju („1 234 zł”, „1.234 lei”). Twarda spacja przed walutą, jak w
// kits/format.js, żeby waluta nie spadała do nowego wiersza. null gdy brak wartości.
export const formatKwota = (value, decimals = 0) => {
  const liczba = formatPrice(value, decimals);
  return liczba === null ? null : `${liczba} ${KRAJ.symbol}`;
};

// ---- Dostępność: normalizacja + klasyfikacja (JEDNO źródło prawdy) ----
// Nowe/nieznane wartości -> 'unknown' (neutralny), nigdy nie psują UI.
// tony: 'high' | 'mid' | 'low' | 'out' | 'order' | 'unknown'
// Uwaga: 'ł'/'Ł' nie rozkłada się w NFD, więc podmieniamy je jawnie.
const stripDiacritics = (s) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l').replace(/Ł/g, 'L');

// Kolejność MA znaczenie (np. "niedostępny" przed "dostępny", "na zamówienie" przed "dostępny").
const AVAILABILITY_RULES = [
  // Wartości schema.org — ładowarki EV biorą dostępność z danych strukturalnych
  // sklepu, więc trafia tu enum ("InStock", "BackOrder"), a nie polski tekst jak
  // w pozostałych kategoriach. Bez tych reguł badge pokazywał surowe "BackOrder".
  // Stoją PRZED regułami tekstowymi, bo "OutOfStock" zawiera "out of stock",
  // ale "LimitedAvailability" trafiłoby najpierw w regułę od "dostep".
  { keys: ['outofstock', 'soldout', 'discontinued'], label: 'Niedostępny', tone: 'out' },
  { keys: ['limitedavailability'], label: 'Na wyczerpaniu', tone: 'low' },
  { keys: ['backorder', 'preorder', 'presale'], label: 'Na zamówienie', tone: 'order' },
  { keys: ['instock', 'instoreonly', 'onlineonly'], label: 'Dostępny', tone: 'high' },

  { keys: ['niedostep', 'wyprzedan', 'brak', 'sold out', 'out of stock'], label: 'Niedostępny', tone: 'out' },
  { keys: ['na wyczerpaniu', 'running out', 'ostatnie', 'konczy', 'niski stan'], label: 'Na wyczerpaniu', tone: 'low' },
  { keys: ['malo'], label: 'Mało', tone: 'low' },
  { keys: ['na zamowienie', 'pod zamowienie', 'on order', 'preorder'], label: 'Na zamówienie', tone: 'order' },
  { keys: ['duza ilosc', 'duzo', 'bardzo duzo', 'wysoka', 'minimum 10', 'min 10', 'high'], label: 'Duża ilość', tone: 'high' },
  { keys: ['srednia', 'avarage', 'average'], label: 'Średnia ilość', tone: 'mid' },
  // "Dostępny" i synonimy oznaczające brak ryzyka niedostępności -> zielony.
  { keys: ['dostep', 'in stock', 'na stanie', 'w magazynie'], label: 'Dostępny', tone: 'high' },

  // Napisy sklepów rumuńskich (bez diakrytyków po normalizacji). Stoją NA KOŃCU, żeby
  // nie zmienić klasyfikacji polskich tekstów, które pasowały do reguł wyżej.
  { keys: ['stoc epuizat', 'indisponibil', 'nu este in stoc', 'fara stoc'], label: 'Niedostępny', tone: 'out' },
  { keys: ['stoc limitat', 'ultimele bucati'], label: 'Na wyczerpaniu', tone: 'low' },
  { keys: ['la comanda', 'precomanda', 'in curand'], label: 'Na zamówienie', tone: 'order' },
  { keys: ['in stoc', 'disponibil'], label: 'Dostępny', tone: 'high' },
];

// Etykieta dostępności w języku wersji serwisu. Reguły wyżej trzymają polskie etykiety
// (to też klucze słownika `wspolne:dostepnosc`), logika korzysta wyłącznie z `tone`.
const etykietaDostepnosci = (label) => i18n.t(`wspolne:dostepnosc.${label}`, { defaultValue: label });

// Progi ilościowe — DECYZJA BIZNESOWA, łatwa do zmiany w jednym miejscu.
// 0 -> niedostępny, 1..3 -> mało, 4..9 -> średnio, >=10 -> dużo.
const QTY_LOW_MAX = 3;
const QTY_MID_MAX = 9;

export const classifyAvailability = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return { label: etykietaDostepnosci('Brak danych'), tone: 'unknown' };
  }
  // 1. normalizacja białych znaków (naprawia np. "127  szt")
  const cleaned = String(value).replace(/\s+/g, ' ').trim();
  // 2. usuń jednostkę na końcu: szt / sztuk / opak / pcs ...
  const noUnit = cleaned.replace(/\s*(szt\.?|sztuki?|sztuk|opak\.?|opakowa\w*|pcs)\.?$/i, '').trim();

  // 3. czysta liczba -> licznik + próg
  if (/^\d+$/.test(noUnit)) {
    const n = parseInt(noUnit, 10);
    if (n <= 0) return { label: etykietaDostepnosci('Niedostępny'), tone: 'out' };
    const tone = n <= QTY_LOW_MAX ? 'low' : n <= QTY_MID_MAX ? 'mid' : 'high';
    return { label: i18n.t('wspolne:sztuk', { n }), tone };
  }

  // 4. słownik synonimów (bez diakrytyków, lowercase)
  const norm = stripDiacritics(cleaned.toLowerCase());
  for (const rule of AVAILABILITY_RULES) {
    if (rule.keys.some((k) => norm.includes(k))) {
      return { label: etykietaDostepnosci(rule.label), tone: rule.tone };
    }
  }

  // 5. nieznane -> neutralny, pokaż oryginał (do wyłapania nowych synonimów)
  return { label: cleaned, tone: 'unknown' };
};

// Sam label (wsteczna zgodność).
export const formatAvailability = (value) => classifyAvailability(value).label;

// Cena za Wp dla paneli: preferuj price_wp z danych, w innym wypadku policz z netto/mocy.
export const formatPricePerWp = (product) => {
  const direct = parseFloat(product?.price_wp);
  let value = !Number.isNaN(direct) && direct > 0 ? direct : null;
  if (value === null) {
    const netto = parseFloat(product?.price_netto);
    const power = parseFloat(product?.power);
    if (!Number.isNaN(netto) && !Number.isNaN(power) && power > 0) {
      value = netto / power;
    }
  }
  if (value === null || Number.isNaN(value)) return null;
  return `${value.toFixed(2).replace('.', ',')} ${KRAJ.symbol}/Wp`;
};

// Seria falownika = opcjonalny 5. segment match_key (mfr|power|type|phase|series),
// wykrywany przez model builder (transform-svc). Backend nie ma jeszcze osobnego
// pola `model_series`/`series` — to tymczasowy, jawnie dopuszczony fallback.
// Brak 5. segmentu (produkt w ogólnej grupie modelowej) -> null, nie placeholder.
export const seriesFromMatchKey = (matchKey) => {
  if (!matchKey || typeof matchKey !== 'string') return null;
  const parts = matchKey.split('|');
  const series = parts.length === 5 ? parts[4].trim() : '';
  return series ? series.replaceAll('_', ' ') : null;
};
